import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, Clock, Play, Pause } from 'lucide-react';
import axios from 'axios';

// Custom hook to fetch system settings
const useSystemSettings = () => {
  const [settings, setSettings] = useState({ idle_threshold_minutes: 5 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/api/system-settings');
        setSettings(response.data);
      } catch (error) {
        // Fallback to default
        setSettings({ idle_threshold_minutes: 5 });
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  return { settings, loading };
};

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};


const useIdleDetection = (idleTime = 300000, enabled = true) => {
  const [isIdle, setIsIdle] = useState(false);
  const [idleStartTime, setIdleStartTime] = useState(null);
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    if (!enabled || idleTime === null) {
      setIsIdle(false);
      setIdleStartTime(null);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      return;
    }
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown', 'input', 'focus'];
    const handleActivity = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      lastActivityRef.current = Date.now();
      setIsIdle(false);
      setIdleStartTime(null);
      timeoutRef.current = setTimeout(() => {
        setIsIdle(true);
        setIdleStartTime(Date.now());
      }, idleTime);
    };
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });
    handleActivity(); // Trigger once initially
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      setIsIdle(false);
      setIdleStartTime(null);
    };
  }, [idleTime, enabled]);
  if (!enabled || idleTime === null) {
    return { isIdle: false, idleStartTime: null, lastActivity: null };
  }
  return { isIdle, idleStartTime, lastActivity: lastActivityRef.current };
};

const IdleDetectionModal = ({ isOpen, onResume, onStop, idleDuration }) => {
  if (!isOpen) return null;

  const formatDuration = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-yellow-100 mb-4">
            <AlertTriangle className="h-6 w-6 text-yellow-600" />
          </div>
          
          <h3 className="text-lg font-semibold text-secondary-900 mb-2">
            Timer Still Running
          </h3>
          
          <p className="text-secondary-600 mb-4">
            You've been idle for {formatDuration(idleDuration)}. 
            Your timer is still running. What would you like to do?
          </p>
          
          <div className="flex gap-3 justify-center">
            <button
              onClick={onResume}
              className="btn-primary flex items-center gap-2"
            >
              <Play className="h-4 w-4" />
              Continue Timer
            </button>
            
            <button
              onClick={onStop}
              className="btn-secondary flex items-center gap-2"
            >
              <Pause className="h-4 w-4" />
              Stop Timer
            </button>
          </div>
          
          
        </div>
      </div>
    </div>
  );
};

const ActivityMonitor = ({ activeTimer, onStopTimer }) => {
  const [showIdleModal, setShowIdleModal] = useState(false);
  const [idleNotificationShown, setIdleNotificationShown] = useState(false);
  const [idlePeriods, setIdlePeriods] = useState([]);
  const idlePeriodsRef = useRef([]);
  const lastIdleStartRef = useRef(null);
  const lastValidIdleStartRef = useRef(null);
  const { settings, loading } = useSystemSettings();
  
  //Use idle threshold from system settings (in minutes), fallback to 5 minutes
  const idleThresholdMs = settings?.idle_threshold_minutes ? settings.idle_threshold_minutes * 60000 : 300000;
  const idleDetectionEnabled = !!activeTimer;
  const idleDetection = useIdleDetection(idleThresholdMs, idleDetectionEnabled);
  const isIdle = idleDetection.isIdle;
  const idleStartTime = idleDetection.idleStartTime;

  
  

  useEffect(() => {
    if (!activeTimer) {
      // Timer is not active — disable everything
      setShowIdleModal(false);
      setIdleNotificationShown(false);
      setIdlePeriods([]);
      lastIdleStartRef.current = null;
      lastValidIdleStartRef.current = null;
    } else {
      // Timer just started — reinitialize detection
      window.dispatchEvent(new Event('mousemove')); // Simulate activity to kick it off
    }
  }, [activeTimer]);

  // All other effects and logic below should be gated by idleDetectionEnabled
  useEffect(() => {
    if (!idleThresholdMs) return;
    
    // When user becomes idle, set both refs if not already set
    if (isIdle && idleStartTime && !lastIdleStartRef.current) {
      lastIdleStartRef.current = idleStartTime;
      lastValidIdleStartRef.current = idleStartTime;
      
      // Show idle modal after a short delay to avoid immediate popup
      setTimeout(() => {
        if (isIdle && !idleNotificationShown) {
          setShowIdleModal(true);
          setIdleNotificationShown(true);
        }
      }, 1000);
    }
    
    // When user becomes active, record the idle period if ref is set
    if (!isIdle && lastIdleStartRef.current !== null) {
      const start = lastValidIdleStartRef.current ?? lastIdleStartRef.current;
      const end = Date.now();
    
      if (start !== null) {
        const newPeriod = { start, end };
        
        idlePeriodsRef.current = [...idlePeriodsRef.current, newPeriod];
        
        setIdlePeriods(idlePeriodsRef.current);
    
        // Save to localStorage with correct timer ID
        const timerId = activeTimer?.id || 'default';
        const periods = getIdlePeriodsFromStorage(timerId);
        periods.push(newPeriod);
        setIdlePeriodsToStorage(timerId, periods);
      }
    
      lastIdleStartRef.current = null;
      lastValidIdleStartRef.current = null;
    }
    
    // When user becomes active, automatically hide the idle modal
    if (!isIdle && showIdleModal) {
      // Small delay to ensure the user's action is intentional
      setTimeout(() => {
        if (!isIdle) { // Double-check that user is still active
          setShowIdleModal(false);
          setIdleNotificationShown(false);
        }
      }, 500);
    }
  }, [isIdle, idleStartTime, idleThresholdMs, idleNotificationShown, showIdleModal]);

  

  // Reset idle periods when timer stops or starts
  const prevActiveTimer = usePrevious(activeTimer);

useEffect(() => {
  if (!activeTimer && prevActiveTimer) {
    setIdlePeriods([]);
    lastIdleStartRef.current = null;
    lastValidIdleStartRef.current = null;
    setShowIdleModal(false);
    setIdleNotificationShown(false);
  } else if (activeTimer && !prevActiveTimer) {
  }
}, [activeTimer]);


  const handleResumeTimer = () => {
    setShowIdleModal(false);
    setIdleNotificationShown(false);
    // Reset idle tracking when user resumes
    lastIdleStartRef.current = null;
    lastValidIdleStartRef.current = null;
    // Simulate activity to reset idle detection
    window.dispatchEvent(new Event('mousemove'));
    toast.success('Timer resumed');
  };

  const handleStopTimer = () => {
    let periods = idlePeriodsRef.current;
  
    if (idleThresholdMs && isIdle && (lastValidIdleStartRef.current || lastIdleStartRef.current)) {
      const start = lastValidIdleStartRef.current ?? lastIdleStartRef.current;
      const end = Date.now();
      const newPeriod = { start, end };
      periods = [...periods, newPeriod];
  
      // Update ref and state
      idlePeriodsRef.current = periods;
      setIdlePeriods(periods);
  
      lastIdleStartRef.current = null;
      lastValidIdleStartRef.current = null;
    }
  
    // Ensure clean data
    periods = periods.filter(p => p.start && p.end);
  
    // Pass idle periods as 2nd arg so stopTimer persists the final idle slice
    onStopTimer(null, periods);
  
    setShowIdleModal(false);
    setIdleNotificationShown(false);
    toast.success('Timer stopped');
  };
  const idleDuration = idleStartTime ? Date.now() - idleStartTime : 0;

  
  return (
    <>
      {/* Idle Status Indicator */}
      {activeTimer && isIdle && !loading && (
        <div className="fixed bottom-4 right-4 bg-yellow-100 border border-yellow-300 rounded-lg p-3 shadow-lg z-40">
          <div className="flex items-center gap-2 text-yellow-800">
            <Clock className="h-4 w-4" />
            <span className="text-sm font-medium">
              Idle for {Math.floor(idleDuration / 60000)}m {Math.floor((idleDuration % 60000) / 1000)}s
            </span>
          </div>
        </div>
      )}

      {/* Idle Detection Active Indicator */}
      {activeTimer && !loading && (
        <div className="fixed bottom-4 left-4 bg-blue-100 border border-blue-300 rounded-lg p-2 shadow-lg z-40">
          <div className="flex items-center gap-2 text-blue-800">
            <Clock className="h-3 w-3" />
            <span className="text-xs font-medium">
              Idle detection active ({Math.round(idleThresholdMs / 60000)}m)
            </span>
          </div>
        </div>
      )}

      {/* Idle Detection Modal */}
      <IdleDetectionModal
        isOpen={showIdleModal}
        onResume={handleResumeTimer}
        onStop={handleStopTimer}
        idleDuration={idleDuration}
      />
    </>
  );
};

const IdleSettingsComponent = () => {
  const [settings, setSettings] = useState({
    enabled: true,
    threshold: 300000, // 5 minutes
    autoStop: false
  });

  useEffect(() => {
    const savedSettings = localStorage.getItem('idleSettings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  const handleSettingChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('idleSettings', JSON.stringify(newSettings));
    toast.success('Idle detection settings updated');
  };

  const thresholdOptions = [
    { value: 180000, label: '3 minutes' },
    { value: 300000, label: '5 minutes' },
    { value: 600000, label: '10 minutes' },
    { value: 900000, label: '15 minutes' },
    { value: 1800000, label: '30 minutes' }
  ];

 

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-secondary-900">Idle Detection</h4>
      
      <div className="space-y-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => handleSettingChange('enabled', e.target.checked)}
            className="rounded border-secondary-300"
          />
          <span className="text-sm">Enable idle detection</span>
        </label>

        {settings.enabled && (
          <>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Idle threshold
              </label>
              <select
                className="input-field"
                value={settings.threshold}
                onChange={(e) => handleSettingChange('threshold', parseInt(e.target.value))}
              >
                {thresholdOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.autoStop}
                onChange={(e) => handleSettingChange('autoStop', e.target.checked)}
                className="rounded border-secondary-300"
              />
              <span className="text-sm">Automatically stop timer when idle</span>
            </label>
          </>
        )}
      </div>
    </div>
  );
};

// Utility to get and set idle periods in localStorage
function getIdlePeriodsFromStorage(timerId) {
  const key = `idle_periods_${timerId}`;
  try {
    return JSON.parse(localStorage.getItem(key)) || [];
  } catch {
    return [];
  }
}
function setIdlePeriodsToStorage(timerId, periods) {
  const key = `idle_periods_${timerId}`;
  localStorage.setItem(key, JSON.stringify(periods));
}
function clearIdlePeriodsFromStorage(timerId) {
  const key = `idle_periods_${timerId}`;
  localStorage.removeItem(key);
}

export { ActivityMonitor, IdleSettingsComponent, useIdleDetection, useSystemSettings, getIdlePeriodsFromStorage, clearIdlePeriodsFromStorage };
